import React, { useEffect, useMemo, useState } from "react";
import {
  Paper,
  Typography,
  Box,
  Grid,
  TextField,
  InputAdornment,
  Button,
} from "@material-ui/core";
import {
  Search as SearchIcon,
  GetApp as ExcelIcon,
  Assignment as AssignmentIcon,
} from "@material-ui/icons";
import DnerhsApi, { CancelToken } from "../../api/DnerhsApi";
import Alert from "../../components/Alert";
import ResponseUtils from "../../utils/ResponseUtils";
import history from "../../utils/History";

import EstadisticasHeader from "./components/EstadisticasHeader";
import FiltrosAvanzados from "./components/FiltrosAvanzados";
import TablaPracticas from "./components/TablaPracticas";

import { calcularEstadisticas, exportToCSV } from "./utils/formatters";
import { filtrarPorBusqueda, filtrarPorFiltrosAvanzados, limpiarFiltros } from "./utils/filters";

import { useStyles } from "./styles/PracticasConvenio.styles";

const opcionesVacias = {
  instituciones: [],
  carreras: [],
  servicios: [],
};

const PracticasConvenioList = () => {
  const classes = useStyles();

  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingOpciones, setLoadingOpciones] = useState(false);
  const [consultaRealizada, setConsultaRealizada] = useState(false);
  const [opcionesFiltro, setOpcionesFiltro] = useState(opcionesVacias);

  const [searchTerm, setSearchTerm] = useState("");
  const [filtros, setFiltros] = useState(limpiarFiltros());

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    loadOpcionesFiltros(filtros);
  }, [filtros.periodoDesde, filtros.periodoHasta]);

  const showServerError = (error) => {
    let message = "Ocurrio un error inesperado, por favor vuelva a reintentar";
    if (error.response) {
      message = ResponseUtils.getMessageError(error.response);
    }

    Alert.show({
      message,
      type: "error",
    });
  };

  const getPeriodoSeleccionado = (filtrosConsulta = filtros) => {
    const { periodoDesde, periodoHasta } = filtrosConsulta;

    const periodo = {};

    if (periodoDesde) {
      const [desdeAnio, desdeMes] = periodoDesde.split("-");
      periodo.desde = `${desdeAnio}-${desdeMes}-01`;
    }

    if (periodoHasta) {
      const [hastaAnio, hastaMes] = periodoHasta.split("-");
      const ultimoDiaHasta = new Date(Number(hastaAnio), Number(hastaMes), 0).getDate();
      periodo.hasta = `${hastaAnio}-${hastaMes}-${String(ultimoDiaHasta).padStart(2, "0")}`;
    }

    if (periodo.desde && periodo.hasta && periodo.desde > periodo.hasta) {
      return null;
    }

    return periodo;
  };

  const buildFiltroUrl = (baseUrl, filtrosConsulta = filtros, includeSelects = true) => {
    const periodo = getPeriodoSeleccionado(filtrosConsulta);
    if (!periodo) return null;

    const params = new URLSearchParams();

    if (periodo.desde) {
      params.append("desde", periodo.desde);
    }
    if (periodo.hasta) {
      params.append("hasta", periodo.hasta);
    }

    if (includeSelects && filtrosConsulta.institucion && filtrosConsulta.institucion !== "todas") {
      params.append("institucion", filtrosConsulta.institucion);
    }
    if (includeSelects && filtrosConsulta.carrera && filtrosConsulta.carrera !== "todas") {
      params.append("carrera", filtrosConsulta.carrera);
    }
    if (includeSelects && filtrosConsulta.servicio && filtrosConsulta.servicio !== "todos") {
      params.append("servicio", filtrosConsulta.servicio);
    }
    if (includeSelects && filtrosConsulta.ciEstudiante && filtrosConsulta.ciEstudiante.trim() !== "") {
      params.append("ciEstudiante", filtrosConsulta.ciEstudiante.trim());
    }
    if (includeSelects && filtrosConsulta.cronogramaId && filtrosConsulta.cronogramaId.trim() !== "") {
      params.append("cronogramaId", filtrosConsulta.cronogramaId.trim());
    }
    if (includeSelects && Array.isArray(filtrosConsulta.estados) && filtrosConsulta.estados.length > 0) {
      filtrosConsulta.estados.forEach((estado) => {
        if (estado) {
          params.append("estado", estado);
        }
      });
    } else if (includeSelects && filtrosConsulta.estado && filtrosConsulta.estado !== "todos") {
      params.append("estado", filtrosConsulta.estado);
    }

    const queryString = params.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  };

  const loadOpcionesFiltros = async (filtrosConsulta) => {
    const url = buildFiltroUrl("/practicas-convenio/opciones-filtros", filtrosConsulta, false);
    if (!url) return;

    setLoadingOpciones(true);
    const source = CancelToken.source();
    try {
      const response = await DnerhsApi.get(url, {
        cancelToken: source.token,
      });

      const nuevasOpciones = {
        instituciones: response.data?.instituciones || [],
        carreras: response.data?.carreras || [],
        servicios: response.data?.servicios || [],
      };

      setOpcionesFiltro(nuevasOpciones);
      setFiltros((prev) => ({
        ...prev,
        institucion:
          prev.institucion && prev.institucion !== "todas" && !nuevasOpciones.instituciones.includes(prev.institucion)
            ? "todas"
            : prev.institucion,
        carrera:
          prev.carrera && prev.carrera !== "todas" && !nuevasOpciones.carreras.includes(prev.carrera)
            ? "todas"
            : prev.carrera,
        servicio:
          prev.servicio && prev.servicio !== "todos" && !nuevasOpciones.servicios.includes(prev.servicio)
            ? "todos"
            : prev.servicio,
      }));
    } catch (error) {
      console.log(error);
      showServerError(error);
    } finally {
      setLoadingOpciones(false);
    }
  };

  const loadData = async () => {
    const url = buildFiltroUrl("/practicas-convenio/filtrar");
    if (!url) {
      Alert.show({
        message: "El rango de mes/anio no es valido",
        type: "warning",
      });
      return;
    }

    setLoading(true);
    const source = CancelToken.source();
    try {
      const response = await DnerhsApi.get(url, {
        cancelToken: source.token,
      });

      if (response.status === 200) {
        setAllRows(response.data || []);
        setConsultaRealizada(true);
      } else {
        Alert.show({
          message: "Error al cargar los datos",
          type: "error",
        });
      }
    } catch (error) {
      console.log(error);
      showServerError(error);
    } finally {
      setLoading(false);
    }
  };

  const rowsFiltradas = useMemo(() => {
    if (!consultaRealizada) return [];

    let resultado = allRows;
    resultado = filtrarPorBusqueda(resultado, searchTerm);
    resultado = filtrarPorFiltrosAvanzados(resultado, filtros);

    return resultado;
  }, [allRows, searchTerm, filtros, consultaRealizada]);

  const stats = useMemo(() => {
    return calcularEstadisticas(rowsFiltradas);
  }, [rowsFiltradas]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleFiltrosChange = (nuevosFiltros) => {
    setFiltros(nuevosFiltros);
    setPage(0);
  };

  const handleBuscar = () => {
    setSearchTerm("");
    setPage(0);
    setAllRows([]);
    loadData();
  };

  const handleLimpiarFiltros = () => {
    setFiltros(limpiarFiltros());
    setSearchTerm("");
    setAllRows([]);
    setPage(0);
    setConsultaRealizada(false);
    setOpcionesFiltro(opcionesVacias);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExportarExcel = () => {
    exportToCSV(rowsFiltradas, "practicas_convenio.xlsx");
    Alert.show({
      message: "Datos exportados exitosamente",
      type: "success",
    });
  };

  const handleVerDetalle = (row) => {
    history.push(`/practicas-convenio/detalle/${row.cronogramaId}`);
  };

  const handleVerConvenio = (convenioId) => {
    history.push(`/practicas-convenio/convenio/${convenioId}`);
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper>
          <Box p={3}>
            <Box className={classes.header}>
              <Typography variant="h5" className={classes.title}>
                <AssignmentIcon className={classes.titleIcon} />
                Practicas de Convenio
              </Typography>

              {!loading && consultaRealizada && <EstadisticasHeader stats={stats} />}
            </Box>

            <Box className={classes.searchSection}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                label="Buscar"
                placeholder="Buscar estudiante, CI, institucion..."
                value={searchTerm}
                onChange={handleSearchChange}
                className={classes.searchField}
                disabled={!consultaRealizada}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <FiltrosAvanzados
              filtros={filtros}
              opciones={opcionesFiltro}
              onFiltrosChange={handleFiltrosChange}
              onBuscar={handleBuscar}
              onLimpiar={handleLimpiarFiltros}
              loading={loading}
              loadingOpciones={loadingOpciones}
            />

            {consultaRealizada && (
              <Box className={classes.resultsToolbar}>
                <Typography variant="subtitle2">
                  Resultados ({rowsFiltradas.length})
                </Typography>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<ExcelIcon />}
                  onClick={handleExportarExcel}
                  className={classes.exportButton}
                  disabled={rowsFiltradas.length === 0}
                >
                  Exportar Excel
                </Button>
              </Box>
            )}

            <TablaPracticas
              rows={rowsFiltradas}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              onVerDetalle={handleVerDetalle}
              onVerConvenio={handleVerConvenio}
              loading={loading}
              consultaRealizada={consultaRealizada}
            />
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default PracticasConvenioList;
